import { selectFetchingVideoSrc } from '../../../../../store/workspace/selectors';
import { getExamFilesThunk } from '../../../../../store/workspace/thunks';
import { useDispatch, useSelector } from '../../../../../store/store';
import MotionWrapper from '../../../../../layouts/MotionWrapper';
import { CodeBlock, dracula } from 'react-code-blocks';
import Button from '../../../../../components/Button';
import styles from './style.module.scss';
import { useState } from 'react';
import {
    CodingExamStatics,
    CodingExamExaminerData,
} from '../../../../../store/workspace/types';
import {
    ChevronUpIcon,
    EmptyStateIcon,
    CheckCircleIcon,
    TimesCircleIcon,
    ChevronDownIcon,
} from '../../../../../assets/icons';

interface Props {
    examData: CodingExamStatics | null;
    examinerData?: CodingExamExaminerData;
}

export default function CodingExamResults(props: Props) {
    const dispatch = useDispatch();
    const { examData, examinerData } = props;
    const fetchingVideoSrc = useSelector(selectFetchingVideoSrc);
    const [fetchingPlayerIndx, setFetchingPlayerIndx] = useState(-1);
    const [openedaAcordions, setOpenedaAcordions] = useState<number[]>([]);
    const search = examinerData ? examData?.exam?.questions.find(i => examinerData.language in i) : undefined;
    const question = (examinerData && search) ? search[examinerData.language] : [];

    const manageAcordions = (index: number) => {
        const check = openedaAcordions.findIndex(i => i === index);
        if (check === -1) {
            setOpenedaAcordions(prev => [...prev, index]);
        } else {
            setOpenedaAcordions(prev => prev.filter(i => i !== index));
        }
    };

    const getVideo = (fileName: string, indx: number) => {
        setFetchingPlayerIndx(indx);
        dispatch(getExamFilesThunk({
            jobId: examData?.exam?.jobId,
            multipleChoiceExamId: null,
            codingExamId: examData?.exam?.id,
            applicantId: examinerData?.userId,
            file: fileName,
        }))
    }

    return (
        <MotionWrapper className={styles.container}>
            {!examinerData ?
                <div className={styles.empty_state}>
                    <EmptyStateIcon />
                    <span>No data.</span>
                </div> :
                <div className={styles.detail}>
                    <div className={styles.header}>
                        <div className={styles.header_top}>
                            <span className={styles.header_top_title}>
                                {examData?.jobInfo.title}
                            </span>
                        </div>
                        <div className={styles.header_bottom}>
                            <span className={styles.header_bottom_item}>
                                Applicant Name : {examinerData?.first_name} {examinerData?.family_name}
                            </span>
                            <span className={styles.header_bottom_item}>
                                Question Quantities : {examData?.exam?.countQuestions}
                            </span>
                            <span className={styles.header_bottom_item}>
                                Exam Time : {examData?.exam?.time}'
                            </span>
                        </div>
                    </div>
                    <div className={styles.question_data}>
                        <table>
                            <thead>
                                <tr>
                                    <th colSpan={1}></th>
                                    <th colSpan={2}>
                                        <span className={styles.head_section}>
                                            Test Cases
                                        </span>
                                    </th>
                                    <th colSpan={2}>
                                        <span className={styles.head_section}>
                                            Complexity
                                        </span>
                                    </th>
                                    <th colSpan={5}>
                                        <span className={styles.head_section}>
                                            Code Quality
                                        </span>
                                    </th>
                                </tr>
                                <tr>
                                    <th>Q</th>
                                    <th>Coverage</th>
                                    <th>Time</th>
                                    <th>Space</th>
                                    <th>Time</th>
                                    <th>Readability</th>
                                    <th>Duplication</th>
                                    <th>Efficiency</th>
                                    <th>Complexity</th>
                                    <th>Maintainability</th>
                                    <th>Verification<br />constraint</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Q1</td>
                                    <td>{examinerData?.scores[0]?.test_coverage}/10</td>
                                    <td>{examinerData?.scores[0]?.execution_time.score}/10</td>
                                    <td>{examinerData?.scores[0]?.space_complexity.score}/10</td>
                                    <td>{examinerData?.scores[0]?.time_complexity.score}/10</td>
                                    <td>{examinerData?.scores[0]?.readability}/10</td>
                                    <td>{examinerData?.scores[0]?.duplication}/10</td>
                                    <td>{examinerData?.scores[0]?.efficiency}/10</td>
                                    <td>{examinerData?.scores[0]?.complexity}/10</td>
                                    <td>{examinerData?.scores[0]?.maintainability}/10</td>
                                    <td>{examinerData?.scores[0]?.verifies_constraints ? <CheckCircleIcon /> : <TimesCircleIcon />}</td>
                                </tr>
                            </tbody>
                        </table>

                        <div className={styles.card}>
                            <div className={styles.card_body}>
                                <div className={styles.card_body_section}>
                                    <div className={styles.card_body_section_title}>
                                        <span>Test Cases</span>
                                    </div>
                                    <div className={styles.card_body_section_content}>
                                        <div className={styles.card_body_section_content_group}>
                                            <div className={styles.card_body_section_content_group_title}>
                                                <span>Q1</span>
                                            </div>
                                            <div className={styles.card_body_section_content_group_details}>
                                                <div className={styles.card_body_section_content_group_details_item}>
                                                    <span>Coverage</span>
                                                    <span>{examinerData?.scores[0]?.test_coverage}/10</span>
                                                </div>
                                                <div className={styles.card_body_section_content_group_details_item}>
                                                    <span>Time</span>
                                                    <span>{examinerData?.scores[0]?.execution_time.score}/10</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.card_body_section}>
                                    <div className={styles.card_body_section_title}>
                                        <span>Complexity</span>
                                    </div>
                                    <div className={styles.card_body_section_content}>
                                        <div className={styles.card_body_section_content_group}>
                                            <div className={styles.card_body_section_content_group_title}>
                                                <span>Q1</span>
                                            </div>
                                            <div className={styles.card_body_section_content_group_details}>
                                                <div className={styles.card_body_section_content_group_details_item}>
                                                    <span>Space</span>
                                                    <span>{examinerData?.scores[0]?.space_complexity.score}/10</span>
                                                </div>
                                                <div className={styles.card_body_section_content_group_details_item}>
                                                    <span>Time</span>
                                                    <span>{examinerData?.scores[0]?.time_complexity.score}/10</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className={styles.card_body_section}>
                                        <div className={styles.card_body_section_title}>
                                            <span>Code Quality</span>
                                        </div>
                                        <div className={styles.card_body_section_content}>
                                            <div className={styles.card_body_section_content_group}>
                                                <div className={styles.card_body_section_content_group_title}>
                                                    <span>Q1</span>
                                                </div>
                                                <div className={styles.card_body_section_content_group_details}>
                                                    <div className={styles.card_body_section_content_group_details_item}>
                                                        <span>Readability</span>
                                                        <span>{examinerData?.scores[0]?.readability}/10</span>
                                                    </div>
                                                    <div className={styles.card_body_section_content_group_details_item}>
                                                        <span>Duplication</span>
                                                        <span>{examinerData?.scores[0]?.duplication}/10</span>
                                                    </div>
                                                    <div className={styles.card_body_section_content_group_details_item}>
                                                        <span>Efficiency</span>
                                                        <span>{examinerData?.scores[0]?.efficiency}/10</span>
                                                    </div>
                                                    <div className={styles.card_body_section_content_group_details_item}>
                                                        <span>Complexity</span>
                                                        <span>{examinerData?.scores[0]?.complexity}/10</span>
                                                    </div>
                                                    <div className={styles.card_body_section_content_group_details_item}>
                                                        <span>Maintainability</span>
                                                        <span>{examinerData?.scores[0]?.maintainability}/10</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={styles.accordion}>
                            <div
                                className={styles.accordion_header}
                                onClick={() => manageAcordions(0)}
                            >
                                <span>Question Description</span>
                                {openedaAcordions.some(i => i === 0) ? <ChevronUpIcon /> : <ChevronDownIcon />}
                            </div>
                            {openedaAcordions.some(i => i === 0) &&
                                <pre className={styles.accordion_body}>
                                    {question[0]?.description}
                                </pre>}
                        </div>
                        <div className={styles.accordion}>
                            <div
                                className={styles.accordion_header}
                                onClick={() => manageAcordions(1)}
                            >
                                <span>Applicant Answer:</span>
                                {openedaAcordions.some(i => i === 1) ? <ChevronUpIcon /> : <ChevronDownIcon />}
                            </div>
                            {openedaAcordions.some(i => i === 1) &&
                                <div className={styles.accordion_body}>
                                    <CodeBlock
                                        text={examinerData?.answer.answers[0]}
                                        language={examinerData?.language}
                                        showLineNumbers={true}
                                        theme={dracula}
                                    />
                                </div>}
                        </div>
                        <div className={styles.accordion}>
                            <div
                                className={styles.accordion_header}
                                onClick={() => manageAcordions(6)}
                            >
                                <span>Test Cases:</span>
                                {openedaAcordions.some(i => i === 6) ? <ChevronUpIcon /> : <ChevronDownIcon />}
                            </div>
                            {openedaAcordions.some(i => i === 6) &&
                                <div className={styles.accordion_body}>
                                    <CodeBlock
                                        text={question[0]?.test_cases}
                                        language={examinerData?.language}
                                        showLineNumbers={true}
                                        theme={dracula}
                                    />
                                </div>}
                        </div>
                        <div className={styles.accordion}>
                            <div
                                className={styles.accordion_header}
                                onClick={() => manageAcordions(9)}
                            >
                                <span>Answer report:</span>
                                {openedaAcordions.some(i => i === 9) ? <ChevronUpIcon /> : <ChevronDownIcon />}
                            </div>
                            {openedaAcordions.some(i => i === 9) &&
                                <div className={styles.accordion_body}>
                                    {examinerData?.scores[0]?.feedback_report}
                                </div>}
                        </div>
                        <div className={styles.accordion}>
                            <div
                                className={styles.accordion_header}
                                onClick={() => manageAcordions(12)}
                            >
                                <span>Applicant Video:</span>
                                <Button
                                    type='Primary'
                                    text='Open Player'
                                    loading={fetchingVideoSrc && fetchingPlayerIndx === 12}
                                    onClick={() => getVideo('stream_1.webm', 12)}
                                    className={styles.accordion_body_open_video}
                                />
                            </div>
                        </div>
                        <div className={styles.accordion}>
                            <div
                                className={styles.accordion_header}
                                onClick={() => manageAcordions(15)}
                            >
                                <span>Screen Record:</span>
                                <Button
                                    type='Primary'
                                    text='Open Player'
                                    loading={fetchingVideoSrc && fetchingPlayerIndx === 15}
                                    onClick={() => getVideo('screen_record_part_1.webm', 15)}
                                    className={styles.accordion_body_open_video}
                                />
                            </div>
                        </div>
                    </div>
                    <div className={styles.question_data}>
                        <table>
                            <thead>
                                <tr>
                                    <th colSpan={1}></th>
                                    <th colSpan={2}>
                                        <span className={styles.head_section}>
                                            Test Cases
                                        </span>
                                    </th>
                                    <th colSpan={2}>
                                        <span className={styles.head_section}>
                                            Complexity
                                        </span>
                                    </th>
                                    <th colSpan={5}>
                                        <span className={styles.head_section}>
                                            Code Quality
                                        </span>
                                    </th>
                                </tr>
                                <tr>
                                    <th>Q</th>
                                    <th>Coverage</th>
                                    <th>Time</th>
                                    <th>Space</th>
                                    <th>Time</th>
                                    <th>Readability</th>
                                    <th>Duplication</th>
                                    <th>Efficiency</th>
                                    <th>Complexity</th>
                                    <th>Maintainability</th>
                                    <th>Verification constraint</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Q2</td>
                                    <td>{examinerData?.scores[1]?.test_coverage}/10</td>
                                    <td>{examinerData?.scores[1]?.execution_time.score}/10</td>
                                    <td>{examinerData?.scores[1]?.space_complexity.score}/10</td>
                                    <td>{examinerData?.scores[1]?.time_complexity.score}/10</td>
                                    <td>{examinerData?.scores[1]?.readability}/10</td>
                                    <td>{examinerData?.scores[1]?.duplication}/10</td>
                                    <td>{examinerData?.scores[1]?.efficiency}/10</td>
                                    <td>{examinerData?.scores[1]?.complexity}/10</td>
                                    <td>{examinerData?.scores[1]?.maintainability}/10</td>
                                    <td>{examinerData?.scores[1]?.verifies_constraints ? <CheckCircleIcon /> : <TimesCircleIcon />}</td>
                                </tr>
                            </tbody>
                        </table>

                        <div className={styles.card}>
                            <div className={styles.card_body}>
                                <div className={styles.card_body_section}>
                                    <div className={styles.card_body_section_title}>
                                        <span>Test Cases</span>
                                    </div>
                                    <div className={styles.card_body_section_content}>
                                        <div className={styles.card_body_section_content_group}>
                                            <div className={styles.card_body_section_content_group_title}>
                                                <span>Q2</span>
                                            </div>
                                            <div className={styles.card_body_section_content_group_details}>
                                                <div className={styles.card_body_section_content_group_details_item}>
                                                    <span>Coverage</span>
                                                    <span>{examinerData?.scores[1]?.test_coverage}/10</span>
                                                </div>
                                                <div className={styles.card_body_section_content_group_details_item}>
                                                    <span>Time</span>
                                                    <span>{examinerData?.scores[1]?.execution_time.score}/10</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.card_body_section}>
                                    <div className={styles.card_body_section_title}>
                                        <span>Complexity</span>
                                    </div>
                                    <div className={styles.card_body_section_content}>
                                        <div className={styles.card_body_section_content_group}>
                                            <div className={styles.card_body_section_content_group_title}>
                                                <span>Q2</span>
                                            </div>
                                            <div className={styles.card_body_section_content_group_details}>
                                                <div className={styles.card_body_section_content_group_details_item}>
                                                    <span>Space</span>
                                                    <span>{examinerData?.scores[1]?.space_complexity.score}/10</span>
                                                </div>
                                                <div className={styles.card_body_section_content_group_details_item}>
                                                    <span>Time</span>
                                                    <span>{examinerData?.scores[1]?.time_complexity.score}/10</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className={styles.card_body_section}>
                                        <div className={styles.card_body_section_title}>
                                            <span>Code Quality</span>
                                        </div>
                                        <div className={styles.card_body_section_content}>
                                            <div className={styles.card_body_section_content_group}>
                                                <div className={styles.card_body_section_content_group_title}>
                                                    <span>Q2</span>
                                                </div>
                                                <div className={styles.card_body_section_content_group_details}>
                                                    <div className={styles.card_body_section_content_group_details_item}>
                                                        <span>Readability</span>
                                                        <span>{examinerData?.scores[1]?.readability}/10</span>
                                                    </div>
                                                    <div className={styles.card_body_section_content_group_details_item}>
                                                        <span>Duplication</span>
                                                        <span>{examinerData?.scores[1]?.duplication}/10</span>
                                                    </div>
                                                    <div className={styles.card_body_section_content_group_details_item}>
                                                        <span>Efficiency</span>
                                                        <span>{examinerData?.scores[1]?.efficiency}/10</span>
                                                    </div>
                                                    <div className={styles.card_body_section_content_group_details_item}>
                                                        <span>Complexity</span>
                                                        <span>{examinerData?.scores[1]?.complexity}/10</span>
                                                    </div>
                                                    <div className={styles.card_body_section_content_group_details_item}>
                                                        <span>Maintainability</span>
                                                        <span>{examinerData?.scores[1]?.maintainability}/10</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={styles.accordion}>
                            <div
                                className={styles.accordion_header}
                                onClick={() => manageAcordions(2)}
                            >
                                <span>Question Description</span>
                                {openedaAcordions.some(i => i === 2) ? <ChevronUpIcon /> : <ChevronDownIcon />}
                            </div>
                            {openedaAcordions.some(i => i === 2) &&
                                <pre className={styles.accordion_body}>
                                    {question[1]?.description}
                                </pre>}
                        </div>
                        <div className={styles.accordion}>
                            <div
                                className={styles.accordion_header}
                                onClick={() => manageAcordions(3)}
                            >
                                <span>Applicant Answer:</span>
                                {openedaAcordions.some(i => i === 3) ? <ChevronUpIcon /> : <ChevronDownIcon />}
                            </div>
                            {openedaAcordions.some(i => i === 3) &&
                                <div className={styles.accordion_body}>
                                    <CodeBlock
                                        text={examinerData?.answer.answers[1]}
                                        language={examinerData?.language}
                                        showLineNumbers={true}
                                        theme={dracula}
                                    />
                                </div>}
                        </div>
                        <div className={styles.accordion}>
                            <div
                                className={styles.accordion_header}
                                onClick={() => manageAcordions(7)}
                            >
                                <span>Test Cases:</span>
                                {openedaAcordions.some(i => i === 7) ? <ChevronUpIcon /> : <ChevronDownIcon />}
                            </div>
                            {openedaAcordions.some(i => i === 7) &&
                                <div className={styles.accordion_body}>
                                    <CodeBlock
                                        text={question[1]?.test_cases}
                                        language={examinerData?.language}
                                        showLineNumbers={true}
                                        theme={dracula}
                                    />
                                </div>}
                        </div>
                        <div className={styles.accordion}>
                            <div
                                className={styles.accordion_header}
                                onClick={() => manageAcordions(10)}
                            >
                                <span>Answer report:</span>
                                {openedaAcordions.some(i => i === 10) ? <ChevronUpIcon /> : <ChevronDownIcon />}
                            </div>
                            {openedaAcordions.some(i => i === 10) &&
                                <div className={styles.accordion_body}>
                                    {examinerData?.scores[1]?.feedback_report}
                                </div>}
                        </div>
                        <div className={styles.accordion}>
                            <div
                                className={styles.accordion_header}
                                onClick={() => manageAcordions(13)}
                            >
                                <span>Applicant Video:</span>
                                <Button
                                    type='Primary'
                                    text='Open Player'
                                    loading={fetchingVideoSrc && fetchingPlayerIndx === 13}
                                    onClick={() => getVideo('stream_2.webm', 13)}
                                    className={styles.accordion_body_open_video}
                                />
                            </div>
                        </div>
                        <div className={styles.accordion}>
                            <div
                                className={styles.accordion_header}
                                onClick={() => manageAcordions(16)}
                            >
                                <span>Screen Record:</span>
                                <Button
                                    type='Primary'
                                    text='Open Player'
                                    loading={fetchingVideoSrc && fetchingPlayerIndx === 16}
                                    onClick={() => getVideo('screen_record_part_2.webm', 16)}
                                    className={styles.accordion_body_open_video}
                                />
                            </div>
                        </div>
                    </div>
                    <div className={styles.question_data}>
                        <table>
                            <thead>
                                <tr>
                                    <th colSpan={1}></th>
                                    <th colSpan={2}>
                                        <span className={styles.head_section}>
                                            Test Cases
                                        </span>
                                    </th>
                                    <th colSpan={2}>
                                        <span className={styles.head_section}>
                                            Complexity
                                        </span>
                                    </th>
                                    <th colSpan={5}>
                                        <span className={styles.head_section}>
                                            Code Quality
                                        </span>
                                    </th>
                                </tr>
                                <tr>
                                    <th>Q</th>
                                    <th>Coverage</th>
                                    <th>Time</th>
                                    <th>Space</th>
                                    <th>Time</th>
                                    <th>Readability</th>
                                    <th>Duplication</th>
                                    <th>Efficiency</th>
                                    <th>Complexity</th>
                                    <th>Maintainability</th>
                                    <th>Verification constraint</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Q3</td>
                                    <td>{examinerData?.scores[2]?.test_coverage}/10</td>
                                    <td>{examinerData?.scores[2]?.execution_time.score}/10</td>
                                    <td>{examinerData?.scores[2]?.space_complexity.score}/10</td>
                                    <td>{examinerData?.scores[2]?.time_complexity.score}/10</td>
                                    <td>{examinerData?.scores[2]?.readability}/10</td>
                                    <td>{examinerData?.scores[2]?.duplication}/10</td>
                                    <td>{examinerData?.scores[2]?.efficiency}/10</td>
                                    <td>{examinerData?.scores[2]?.complexity}/10</td>
                                    <td>{examinerData?.scores[2]?.maintainability}/10</td>
                                    <td>{examinerData?.scores[2]?.verifies_constraints ? <CheckCircleIcon /> : <TimesCircleIcon />}</td>
                                </tr>
                            </tbody>
                        </table>

                        <div className={styles.card}>
                            <div className={styles.card_body}>
                                <div className={styles.card_body_section}>
                                    <div className={styles.card_body_section_title}>
                                        <span>Test Cases</span>
                                    </div>
                                    <div className={styles.card_body_section_content}>
                                        <div className={styles.card_body_section_content_group}>
                                            <div className={styles.card_body_section_content_group_title}>
                                                <span>Q3</span>
                                            </div>
                                            <div className={styles.card_body_section_content_group_details}>
                                                <div className={styles.card_body_section_content_group_details_item}>
                                                    <span>Coverage</span>
                                                    <span>{examinerData?.scores[2]?.test_coverage}/10</span>
                                                </div>
                                                <div className={styles.card_body_section_content_group_details_item}>
                                                    <span>Time</span>
                                                    <span>{examinerData?.scores[2]?.execution_time.score}/10</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.card_body_section}>
                                    <div className={styles.card_body_section_title}>
                                        <span>Complexity</span>
                                    </div>
                                    <div className={styles.card_body_section_content}>
                                        <div className={styles.card_body_section_content_group}>
                                            <div className={styles.card_body_section_content_group_title}>
                                                <span>Q3</span>
                                            </div>
                                            <div className={styles.card_body_section_content_group_details}>
                                                <div className={styles.card_body_section_content_group_details_item}>
                                                    <span>Space</span>
                                                    <span>{examinerData?.scores[2]?.space_complexity.score}/10</span>
                                                </div>
                                                <div className={styles.card_body_section_content_group_details_item}>
                                                    <span>Time</span>
                                                    <span>{examinerData?.scores[2]?.time_complexity.score}/10</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className={styles.card_body_section}>
                                        <div className={styles.card_body_section_title}>
                                            <span>Code Quality</span>
                                        </div>
                                        <div className={styles.card_body_section_content}>
                                            <div className={styles.card_body_section_content_group}>
                                                <div className={styles.card_body_section_content_group_title}>
                                                    <span>Q3</span>
                                                </div>
                                                <div className={styles.card_body_section_content_group_details}>
                                                    <div className={styles.card_body_section_content_group_details_item}>
                                                        <span>Readability</span>
                                                        <span>{examinerData?.scores[2]?.readability}/10</span>
                                                    </div>
                                                    <div className={styles.card_body_section_content_group_details_item}>
                                                        <span>Duplication</span>
                                                        <span>{examinerData?.scores[2]?.duplication}/10</span>
                                                    </div>
                                                    <div className={styles.card_body_section_content_group_details_item}>
                                                        <span>Efficiency</span>
                                                        <span>{examinerData?.scores[2]?.efficiency}/10</span>
                                                    </div>
                                                    <div className={styles.card_body_section_content_group_details_item}>
                                                        <span>Complexity</span>
                                                        <span>{examinerData?.scores[2]?.complexity}/10</span>
                                                    </div>
                                                    <div className={styles.card_body_section_content_group_details_item}>
                                                        <span>Maintainability</span>
                                                        <span>{examinerData?.scores[2]?.maintainability}/10</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={styles.accordion}>
                            <div
                                className={styles.accordion_header}
                                onClick={() => manageAcordions(4)}
                            >
                                <span>Question Description</span>
                                {openedaAcordions.some(i => i === 4) ? <ChevronUpIcon /> : <ChevronDownIcon />}
                            </div>
                            {openedaAcordions.some(i => i === 4) &&
                                <pre className={styles.accordion_body}>
                                    {question[2]?.description}
                                </pre>}
                        </div>
                        <div className={styles.accordion}>
                            <div
                                className={styles.accordion_header}
                                onClick={() => manageAcordions(5)}
                            >
                                <span>Applicant Answer:</span>
                                {openedaAcordions.some(i => i === 5) ? <ChevronUpIcon /> : <ChevronDownIcon />}
                            </div>
                            {openedaAcordions.some(i => i === 5) &&
                                <div className={styles.accordion_body}>
                                    <CodeBlock
                                        text={examinerData?.answer.answers[2]}
                                        language={examinerData?.language}
                                        showLineNumbers={true}
                                        theme={dracula}
                                    />
                                </div>}
                        </div>
                        <div className={styles.accordion}>
                            <div
                                className={styles.accordion_header}
                                onClick={() => manageAcordions(8)}
                            >
                                <span>Test Cases:</span>
                                {openedaAcordions.some(i => i === 8) ? <ChevronUpIcon /> : <ChevronDownIcon />}
                            </div>
                            {openedaAcordions.some(i => i === 8) &&
                                <div className={styles.accordion_body}>
                                    <CodeBlock
                                        text={question[2]?.test_cases}
                                        language={examinerData?.language}
                                        showLineNumbers={true}
                                        theme={dracula}
                                    />
                                </div>}
                        </div>
                        <div className={styles.accordion}>
                            <div
                                className={styles.accordion_header}
                                onClick={() => manageAcordions(11)}
                            >
                                <span>Answer report:</span>
                                {openedaAcordions.some(i => i === 11) ? <ChevronUpIcon /> : <ChevronDownIcon />}
                            </div>
                            {openedaAcordions.some(i => i === 11) &&
                                <div className={styles.accordion_body}>
                                    {examinerData?.scores[2]?.feedback_report}
                                </div>}
                        </div>
                        <div className={styles.accordion}>
                            <div
                                className={styles.accordion_header}
                                onClick={() => manageAcordions(14)}
                            >
                                <span>Applicant Video:</span>
                                <Button
                                    type='Primary'
                                    text='Open Player'
                                    loading={fetchingVideoSrc && fetchingPlayerIndx === 14}
                                    onClick={() => getVideo('stream_3.webm', 14)}
                                    className={styles.accordion_body_open_video}
                                />                            </div>
                        </div>
                        <div className={styles.accordion}>
                            <div
                                className={styles.accordion_header}
                                onClick={() => manageAcordions(17)}
                            >
                                <span>Screen Record:</span>
                                <Button
                                    type='Primary'
                                    text='Open Player'
                                    loading={fetchingVideoSrc && fetchingPlayerIndx === 17}
                                    onClick={() => getVideo('screen_record_part_3.webm', 17)}
                                    className={styles.accordion_body_open_video}
                                />
                            </div>
                        </div>
                    </div>
                </div>}
        </MotionWrapper>
    )
};
